import {
  AbsenceListDto,
  QueryFactory,
  ResourceDto,
  ResourceType,
} from '@lmt/shared-types';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useUniSelector } from '@lmt/unicorn-shared/app/store/types';
import React, { FC, MouseEventHandler, useMemo } from 'react';
import { AppointmentsBase } from '../../common/devexpress/Scheduler/dx-react-scheduler';
import styles from './AbsenceCalendarAppointment.module.scss';

interface ResourceGroupProps {
  title: string;
  resources: ResourceDto[];
}

interface AbsenceProps {
  onAbsenceMouseEnter?: (
    target: HTMLElement,
    absence: AbsenceListDto[],
  ) => void;
  onAbsenceMouseLeave?: () => void;
  onMouseOver?: MouseEventHandler<HTMLElement>;
  onMouseLeave?: MouseEventHandler<HTMLElement>;
}

export const AbsenceCalendarAppointment: FC<
  AppointmentsBase.AppointmentProps & AbsenceProps
> = (props) => {
  const absenceData = props.data?.data as AbsenceListDto[];
  const i18next = useScopedTranslation('absences');
  const forms = useGlobalForms();
  const claims = useClaims();

  const resourceQuery = QueryFactory.ResourceQuery.useGetResourcesQuery({
    includeDeleted: true,
  });
  const resources = resourceQuery.data?.data ?? [];

  const combinedResources = useMemo(() => {
    const resourceIds = absenceData
      ? [...absenceData.map((x) => x.resourceIds)].flat()
      : [];
    if (!resourceIds || !resources.length) {
      return {
        places: [],
        devices: [],
        doctors: [],
      };
    }
    const filteredResources = (
      resourceIds
        .map((id) => resources.find((resource) => resource.id === id))
        .filter((x) => x) as ResourceDto[]
    ).sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase()
        ? 1
        : b.name.toLowerCase() > a.name.toLowerCase()
          ? -1
          : 0,
    );

    return {
      places: filteredResources.filter((x) => x.type === ResourceType.Places),
      devices: filteredResources.filter(
        (x) => x.type === ResourceType.SpecialConditions,
      ),
      doctors: filteredResources.filter((x) => x.type === ResourceType.Doctors),
    };
  }, [resources, absenceData]);

  return (
    <div
      className={styles.container}
      onDoubleClick={
        !claims.permissions.manageAbsences || absenceData.length > 1
          ? undefined
          : () => {
              forms.openAbsenceEditing(absenceData[0].id);
            }
      }
      onMouseEnter={(e) => {
        const target = e.currentTarget.parentElement;
        if (!target) {
          return;
        }
        props.onAbsenceMouseEnter?.(target, absenceData);
      }}
      onMouseLeave={props.onMouseLeave}
      onMouseOver={props.onMouseOver}
    >
      <div className={styles.absenceInfo}>
        <ResourceGroup
          title={i18next.t('doctor_unavailable')}
          resources={combinedResources.doctors}
        />
        <ResourceGroup
          title={i18next.t('resource_unavailable')}
          resources={[
            ...combinedResources.places,
            ...combinedResources.devices,
          ]}
        />
      </div>
    </div>
  );
};

const ResourceGroup: FC<ResourceGroupProps> = (props) => {
  if (!props.resources.length) {
    return null;
  }
  return (
    <div
      className={styles.resourceGroup}
      data-test-id="resource-unavailability-in-schedule-table"
    >
      <span className={styles.title}>{props.title}</span>
      {props.resources.map((resource) => (
        <span key={resource.id} className={styles.resourceTitle}>
          {resource.name}
        </span>
      ))}
    </div>
  );
};
